// Defer Due Date Panel
// Pull-out panel for deferring compliance due dates

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { format } from 'date-fns';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CalendarIcon, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ComplianceItemCard } from './compliance-item-card';
import { useNavigate } from 'react-router-dom';

// Form validation schema
const formSchema = z.object({
  newDueDate: z.date({
    message: 'New due date is required',
  }),
  reason: z.string().min(10, 'Reason must be at least 10 characters'),
});

type FormValues = z.infer<typeof formSchema>;

interface ComplianceItem {
  id: number;
  assetId?: string;
  assetName: string;
  assetCode?: string;
  regimeCode?: string;
  regimeName: string;
  complianceCheckId?: number;
  currentDueDate: string;
}

interface DeferDueDatePanelProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly complianceItem?: ComplianceItem;
  readonly onSubmit: (data: FormValues) => Promise<void>;
}

export function DeferDueDatePanel({
  open,
  onOpenChange,
  complianceItem,
  onSubmit,
}: Readonly<DeferDueDatePanelProps>) {
  const navigate = useNavigate();
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      newDueDate: undefined,
      reason: '',
    },
  });

  const handleSubmit = async (data: FormValues) => {
    try {
      await onSubmit(data);
      form.reset();
      onOpenChange(false);
    } catch (error) {
      console.error('Error deferring due date:', error);
    }
  };

  const handleNavigateToAsset = (assetId: string) => {
    navigate(`/assets/${assetId}`);
  };

  const handleNavigateToRule = (ruleId: number) => {
    navigate(`/compliance/regulations?ruleId=${ruleId}`);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[540px] flex flex-col p-0">
        <SheetHeader className="px-6 pt-6 pb-4 bg-white dark:bg-black border-b">
          <SheetTitle className="text-lg font-semibold">Defer Due Date</SheetTitle>
          <SheetDescription>
            Postpone the compliance due date for this requirement
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 bg-muted/30">
          <Form {...form}>
            <form id="defer-due-date-form" onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6 pb-6 pt-6">
              {/* Compliance Item Info */}
              {complianceItem && (
                <ComplianceItemCard
                  assetId={complianceItem.assetId}
                  assetName={complianceItem.assetName}
                  assetCode={complianceItem.assetCode}
                  regimeCode={complianceItem.regimeCode || 'N/A'}
                  regimeName={complianceItem.regimeName}
                  complianceCheckId={complianceItem.complianceCheckId}
                  onNavigateToAsset={handleNavigateToAsset}
                  onNavigateToRule={handleNavigateToRule}
                />
              )}

              {/* Warning Alert */}
              <Alert variant="warning">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Deferring a due date should only be done with proper authorization. 
                  Ensure you have documented approval before proceeding.
                </AlertDescription>
              </Alert>

              {/* New Due Date */}
              <FormField
                control={form.control}
                name="newDueDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel className="font-semibold">New Due Date *</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              'w-full pl-3 text-left font-normal bg-white dark:bg-gray-950',
                              !field.value && 'text-muted-foreground'
                            )}
                          >
                            {field.value ? (
                              format(field.value, 'PPP')
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          disabled={(date) => 
                            complianceItem 
                              ? date <= new Date(complianceItem.currentDueDate)
                              : date < new Date()
                          }
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormDescription className="text-xs">
                      Select the new due date (must be after current due date)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Reason */}
              <FormField
                control={form.control}
                name="reason"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold">Reason for Deferral *</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Provide a detailed reason for deferring this due date..."
                        className="min-h-[120px] bg-white dark:bg-gray-950"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription className="text-xs">
                      Explain why this due date needs to be deferred (minimum 10 characters)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </div>

        <SheetFooter className="border-t bg-white dark:bg-black px-6 py-4 mt-auto">
          <div className="flex gap-3 w-full">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="defer-due-date-form"
              className="flex-1"
            >
              Defer Due Date
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
